import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, MessageCircle, MapPin } from "lucide-react";
import api from "@/lib/api";
import { formatINR } from "@/lib/utils-format";
import Seo, { faqSchema, breadcrumbSchema, orgSchema } from "@/components/Seo";
import WhatsAppCTA from "@/components/WhatsAppCTA";

const SITE = "https://signiastylettoix.com";

export default function LocalePage() {
    const { loc, prod } = useParams();
    const [page, setPage] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setPage(null);
        const url = prod ? `/local/page/${loc}/${prod}` : `/local/hub/${loc}`;
        api.get(url)
            .then((r) => { if (!cancelled) setPage(r.data); })
            .catch(() => { if (!cancelled) setError(true); });
        return () => { cancelled = true; };
    }, [loc, prod]);

    if (error) return <div className="min-h-[60vh] flex items-center justify-center text-[#4a5568]">Page not found. <Link className="underline ml-2" to="/hearing-aids">Browse all locations</Link></div>;
    if (!page) return <div className="min-h-[60vh] flex items-center justify-center text-[#4a5568]">Loading…</div>;

    const url = `${SITE}${page.url}`;
    const crumbs = [
        { name: "Home", url: SITE + "/" },
        { name: page.location.city_name, url: `${SITE}/hearing-aids/${page.location.city_slug}` },
        { name: prod ? page.product.name : page.location.name, url },
    ];
    const schema = {
        "@context": "https://schema.org",
        "@graph": [
            orgSchema(),
            breadcrumbSchema(crumbs),
            faqSchema(page.faqs || []),
            ...(page.product ? [{
                "@type": "Product", name: page.product.name, image: page.product.image,
                brand: { "@type": "Brand", name: "Signia" },
                description: page.meta_description,
                offers: { "@type": "Offer", priceCurrency: "INR", price: page.product.price, availability: "https://schema.org/InStock", url },
            }] : []),
        ],
    };

    return (
        <>
            <Seo title={page.title} description={page.meta_description} canonical={url} schema={schema} />
            <section className="pt-12 md:pt-16 pb-16">
                <div className="container-x">
                    <nav className="text-[12.5px] text-[#4a5568] mb-6" aria-label="Breadcrumb">
                        <Link to="/" className="hover:text-[#003E4D]">Home</Link>
                        <span className="mx-2 text-[#9CA3AF]">/</span>
                        <Link to={`/hearing-aids/${page.location.city_slug}`} className="hover:text-[#003E4D]">{page.location.city_name}</Link>
                        <span className="mx-2 text-[#9CA3AF]">/</span>
                        <span className="text-[#1a1a1a]">{prod ? page.product.name : page.location.name}</span>
                    </nav>
                    <p className="eyebrow inline-flex items-center gap-2"><MapPin size={12} /> {page.eyebrow}</p>
                    <h1 className="font-serif text-[36px] md:text-[52px] leading-[1.08] mt-4 max-w-[900px]" data-testid="locale-h1">{page.h1}</h1>
                    <div className="mt-6 text-[16.5px] text-[#4a5568] leading-relaxed max-w-[780px]" dangerouslySetInnerHTML={{ __html: page.intro }} />
                    <div className="mt-8 flex flex-wrap items-center gap-3">
                        <WhatsAppCTA message={`Hi, I'm interested in ${prod ? page.product.name : "Signia hearing aids"} in ${page.location.name}.`} sourcePage={page.url} className="btn-whatsapp" data-testid="locale-whatsapp-cta">
                            <MessageCircle size={16} /> Get Best Price on WhatsApp
                        </WhatsAppCTA>
                        <Link to="/book-trial" className="btn-primary" data-testid="locale-trial-cta">Book Free Trial <ArrowRight size={16} /></Link>
                    </div>

                    {page.product && (
                        <div className="mt-10 p-6 bg-white border border-[#e2ddd4] rounded-2xl max-w-[780px]" data-testid="locale-price-card">
                            <div className="text-[26px] font-serif text-[#003E4D]">{formatINR(page.product.price)}</div>
                            <div className="text-[13.5px] text-[#4a5568] mt-1">{page.product.technology_level} tier · {page.product.hearing_loss_range}</div>
                            <Link to={`/products/${page.product.slug}`} className="btn-primary mt-4 inline-flex">View Product & Buy <ArrowRight size={15} /></Link>
                        </div>
                    )}

                    {page.products && (
                        <div className="mt-10 overflow-x-auto max-w-[840px]">
                            <table className="w-full text-left text-[15px]" data-testid="locale-hub-table">
                                <thead><tr className="border-b border-[#e2ddd4]"><th className="py-3 pr-4">Model</th><th className="py-3 pr-4">Price</th><th className="py-3">Tier</th></tr></thead>
                                <tbody>
                                    {page.products.map((p) => (
                                        <tr key={p.slug} className="border-b border-[#efece6]">
                                            <td className="py-3 pr-4"><Link to={`/hearing-aids/${page.location.slug}/${p.slug}`} className="text-[#0F6B7D] underline">{p.name}</Link></td>
                                            <td className="py-3 pr-4">{formatINR(p.price)}</td>
                                            <td className="py-3">{p.technology_level || "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <div className="mt-12 max-w-[820px] space-y-10">
                        {(page.sections || []).map((s, i) => (
                            <div key={i}>
                                <h2 className="font-serif text-[26px] md:text-[30px] text-[#003E4D] mb-3">{s.h2}</h2>
                                <div className="prose-seo text-[16px] text-[#333] leading-relaxed space-y-3" dangerouslySetInnerHTML={{ __html: s.body }} />
                            </div>
                        ))}
                    </div>

                    {(page.faqs || []).length > 0 && (
                        <div className="mt-14 max-w-[820px]">
                            <h2 className="font-serif text-[28px] text-[#003E4D] mb-4">Frequently asked questions</h2>
                            {page.faqs.map((f, i) => (
                                <details key={i} className="bg-white border border-[#e2ddd4] rounded-xl px-5 py-4 my-2">
                                    <summary className="cursor-pointer font-medium text-[15.5px]">{f.q}</summary>
                                    <p className="mt-2 text-[15px] text-[#4a5568]">{f.a}</p>
                                </details>
                            ))}
                        </div>
                    )}

                    <div className="mt-14 max-w-[860px]">
                        <h2 className="font-serif text-[24px] text-[#003E4D] mb-4">Explore more</h2>
                        <div className="flex flex-wrap gap-2">
                            {(page.links?.siblings || []).map((s) => (
                                <Link key={s.slug} to={prod ? `/hearing-aids/${s.slug}/${s.product_slug}` : `/hearing-aids/${s.slug}`} className="text-[13px] bg-white border border-[#e2ddd4] rounded-full px-4 py-2 text-[#003E4D] hover:border-[#003E4D]">{s.name}</Link>
                            ))}
                            {(page.links?.other_products || []).map((p) => (
                                <Link key={p.slug} to={`/hearing-aids/${loc}/${p.slug}`} className="text-[13px] bg-white border border-[#e2ddd4] rounded-full px-4 py-2 text-[#003E4D] hover:border-[#003E4D]">{p.name}</Link>
                            ))}
                            <Link to={`/hearing-aids/${page.location.city_slug}`} className="text-[13px] bg-white border border-[#e2ddd4] rounded-full px-4 py-2 text-[#003E4D] hover:border-[#003E4D]">All in {page.location.city_name}</Link>
                            <Link to="/learn" className="text-[13px] bg-white border border-[#e2ddd4] rounded-full px-4 py-2 text-[#003E4D] hover:border-[#003E4D]">Knowledge Centre</Link>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
